<template>
  <v-col cols="12" sm="6" md="6" lg="3">
    <v-card>
      <v-progress-linear
        color="primary"
        indeterminate
        v-if="$apollo.loading"
      ></v-progress-linear>
      <v-toolbar flat dense>
        <span class="text-subtitle-1"
          ><router-link
            class="unstyled-link"
            :to="`/org/${currentUser.organization.id}/intake`"
            >New Cases in Period</router-link
          ></span
        >
      </v-toolbar>
      <div class="stat-data">
        <span class="main-stat" :class="getTotalTextClass()">{{
          retainedCasesInPeriod
        }}</span>
        <span class="goal-text">/ {{ totalGoal }}</span>
        <span
          class="stat-data-desc"
          v-for="(item, index) in mainOfficesWithGoals"
          :key="index"
        >
          <span class="office-name">{{ item.shortName }}</span> (<span
            class="office-count"
            :class="item.textClass"
            >{{ item.count }}</span
          >)
        </span>
      </div>
    </v-card>
  </v-col>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import { formatOffice } from "../util";
import differenceInBusinessDays from "date-fns/differenceInBusinessDays";
import addDays from "date-fns/addDays";

export default {
  apollo: {
    organization: {
      query: gql`
        query OrgHomeStatsNewCases(
          $id: ID!
          $startDate: String!
          $endDate: String!
        ) {
          organization(id: $id) {
            id
            retainedCasesInPeriod(startDate: $startDate, endDate: $endDate)
            retainedCasesInPeriodByOffice(
              startDate: $startDate
              endDate: $endDate
            ) {
              office
              count
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
          startDate: this.startDate,
          endDate: this.endDate,
        };
      },
      skip() {
        return this.startDate === null || this.endDate === null;
      },
    },
  },
  computed: {
    ...mapGetters(["currentUser", "startDate", "endDate"]),
    retainedCasesInPeriod() {
      if (!this.organization) return 0;
      return this.organization.retainedCasesInPeriod;
    },
    retainedCasesInPeriodByOffice() {
      if (!this.organization) return [];
      return this.organization.retainedCasesInPeriodByOffice;
    },
    businessDays() {
      let start = this.startDate.split("-").map((x) => parseInt(x));
      let startDate = new Date(start[0], start[1] - 1, start[2]);
      let end = this.endDate.split("-").map((x) => parseInt(x));
      let endDate = new Date(end[0], end[1] - 1, end[2]);
      // We only want to count business days up to today if range is current month
      // If historical range we will count business days through range
      if (endDate > new Date()) {
        endDate = new Date();
      }
      // Add a day to endDate so we can include the last day
      endDate = addDays(endDate, 1);

      return differenceInBusinessDays(endDate, startDate);
    },
    totalGoal() {
      // Fixed monthly goal of 268 cases
      const monthlyGoal = 300;

      // Get the actual number of business days in the month being viewed
      let start = this.startDate.split("-").map((x) => parseInt(x));
      let startOfMonth = new Date(start[0], start[1] - 1, 1);
      let endOfMonth = new Date(start[0], start[1], 0); // Last day of month

      const businessDaysInMonth = differenceInBusinessDays(
        addDays(endOfMonth, 1),
        startOfMonth
      );

      // Calculate what fraction of the month this period represents
      const periodFraction = this.businessDays / businessDaysInMonth;

      // Apply fraction to monthly goal and round up
      return Math.ceil(monthlyGoal * periodFraction);
    },
    mainOfficesWithGoals() {
      if (!this.organization) return [];

      const officeGoals = {
        "(1 - UT Main) Sandy Office": { shortName: "Sandy", monthlyGoal: 143 },
        "(2 - WA Main) Spokane Valley Office": {
          shortName: "Spokane",
          monthlyGoal: 92,
        },
        "(3 - ID Main) Meridian Office": {
          shortName: "Boise",
          monthlyGoal: 54,
        },
        "Sea-Tac": {
          shortName: "Sea-Tac",
          monthlyGoal: 14,
        },
      };

      // Get the actual number of business days in the month being viewed
      let start = this.startDate.split("-").map((x) => parseInt(x));
      let startOfMonth = new Date(start[0], start[1] - 1, 1);
      let endOfMonth = new Date(start[0], start[1], 0); // Last day of month

      const businessDaysInMonth = differenceInBusinessDays(
        addDays(endOfMonth, 1),
        startOfMonth
      );
      const periodFraction = this.businessDays / businessDaysInMonth;

      return this.organization.retainedCasesInPeriodByOffice
        .filter((office) => officeGoals[office.office])
        .map((office) => {
          const config = officeGoals[office.office];
          const periodGoal = Math.ceil(config.monthlyGoal * periodFraction);
          const percentage = (office.count / periodGoal) * 100;

          let textClass = "error--text";
          if (percentage >= 100) textClass = "success--text";
          else if (percentage >= 80) textClass = "primary--text";
          else if (percentage >= 60) textClass = "warning--text";

          return {
            shortName: config.shortName,
            count: office.count,
            goal: periodGoal,
            textClass: textClass,
          };
        });
    },
  },
  methods: {
    formatOffice(office) {
      return formatOffice(office);
    },
    getTotalTextClass() {
      if (this.totalGoal === 0) return "";
      const percentage = (this.retainedCasesInPeriod / this.totalGoal) * 100;

      if (percentage >= 100) return "success--text";
      if (percentage >= 80) return "primary--text";
      if (percentage >= 60) return "warning--text";
      return "error--text";
    },
  },
};
</script>

<style lang="scss">
.unstyled-link {
  color: rgba(0, 0, 0, 0.87) !important;
  text-decoration: none !important;

  &:hover {
    color: rgba(0, 0, 0, 0.6) !important;
    text-decoration: underline !important;
  }

  &:visited {
    color: rgba(0, 0, 0, 0.87) !important;
  }
}

// Enhanced typography for better readability
.main-stat {
  font-size: 2rem !important;
  font-weight: 600 !important;
  line-height: 1.2;
}

.goal-text {
  font-size: 0.875rem;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  margin-left: 4px;
}

.office-name {
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.office-count {
  font-weight: 600 !important;
}

.stat-data-desc {
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.75) !important;
}
</style>
